import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React from 'react';
import {GeoJSON, Map, ScaleControl, TileLayer, WMSTileLayer, ZoomControl} from 'react-leaflet';
import Control from 'react-leaflet-control';
import LayerGroup from 'react-leaflet/lib/LayerGroup';
import {connect} from 'react-redux';
import {
  mapRecentred,
  resetMap,
  setActivePoint,
  setCursorPoint,
  setMapBoundsAndZoom,
  setMapCenter,
  startLocationSearch,
  stopLocationSearch,
  warning
} from '../actions';
import '../components/leaflet/leaflet-windbar';
import VelocityLayer from '../components/leaflet/VelocityLayer';
import {geoserverBase} from '../config/environment.js';
import mapConfig from '../config/map_settings';
import {OVERLAY_KEYS} from '../Constants';
import {utcToLocal} from '../utils/time_utils.js';
import CoordinatesControl from './CoordinatesControl';
import './MapLayers.css';
import IncidentsLayer from './IncidentsLayer';
import IndicatorOverlays from './IndicatorOverlays';
import ManualWeatherLayer from './ManualWeatherLayer';
import PointDetails from './PointDetails';

class MapLayers extends React.Component {

  constructor(props) {
    super(props);
    this.display_time = props.time.display_time;
    this.mapRef = React.createRef();
    this.boundaryMarkers = [];
  }

  addBoundaryLabel = (feature, layer, boundary) => {
    const marker = L.marker(layer.getBounds().getCenter(), {
      icon: L.divIcon({
        html: `<b style="background-color:${boundary.style.color}">${feature.properties[boundary.labelProperty]}</b>`,
        iconSize: 'auto',
        className: 'boundary-label'
      })
    });
    marker.addTo(this.mapRef.current.leafletElement);

    const prefix = boundary.prefix;
    if (!this.boundaryMarkers[prefix]) {
      this.boundaryMarkers[prefix] = [];
    }
    this.boundaryMarkers[prefix].push(marker);
  };

  removeInactiveBoundaryMarkers = (boundaries) => {
    boundaries.forEach(boundary => {
      if (!boundary.active && this.boundaryMarkers[boundary.prefix]) {
        this.boundaryMarkers[boundary.prefix].forEach(marker => marker.remove());
        delete this.boundaryMarkers[boundary.prefix];
      }
    });
  };

  onMapClick = (e) => {
    const { dispatch } = this.props;
    dispatch(setActivePoint(e.latlng.lat, e.latlng.lng));
  };

  onMapChange = (e) => {
    const { dispatch } = this.props;
    const map = e.target;
    dispatch(setMapBoundsAndZoom(map.getBounds(), map._zoom));
    dispatch(setMapCenter(map.getCenter().lat, map.getCenter().lng));
  };

  onMapLoaded = (e) => {
    const { dispatch } = this.props;
    const map = e.target._map;

    // I pass true to the src_location_change parameter so that the URL does not get changed on Map Load.
    dispatch(setMapBoundsAndZoom(map.getBounds(), map._zoom, true));
    dispatch(setMapCenter(map.getCenter().lat, map.getCenter().lng, true));
  };

  onLocationSearch = () => {
    const { dispatch } = this.props;
    dispatch(startLocationSearch());
    navigator.geolocation.getCurrentPosition(
      position => {
        dispatch(setActivePoint(position.coords.latitude, position.coords.longitude, true, true));
        dispatch(setMapCenter(position.coords.latitude, position.coords.longitude));
        dispatch(stopLocationSearch());
      },
      () => {
        dispatch(warning('Failed to find current position.'));
        dispatch(stopLocationSearch());
      }
    );
  };

  onMouseMoveHandler = (e) => {
    const { dispatch } = this.props;
    dispatch(setCursorPoint(e.latlng.lat.toFixed(3), e.latlng.lng.toFixed(3)));
  };

  componentDidUpdate() {
    // When the recentre_map state is set true, we issue an event to Leaflet to recentre the map.
    if (this.props.recentre_map) {
      setTimeout(() => {
        if (this.mapRef && this.mapRef.current) {
          this.mapRef.current.leafletElement.invalidateSize();
        }
      }, 300);
      this.props.dispatch(mapRecentred());
    }
  }

  render() {
    const {
      boundaries,
      active_indicator,
      time,
      map_center,
      map_zoom,
      min_zoom,
      max_zoom,
      active_location_search,
      playing,
      dragging,
      dispatch
    } = this.props;
    const active_boundaries = boundaries.filter(layer => layer.active === true);
    const wms_time = active_indicator && active_indicator.local_time_for_wms ?
                     utcToLocal(time.display_time) :
                     time.display_time;

    this.removeInactiveBoundaryMarkers(boundaries);

    return (
      <div id="mapUI">
        <Map
          center={map_center}
          zoom={parseInt(map_zoom, 10)}
          minZoom={min_zoom}
          maxZoom={max_zoom}
          zoomControl={false}
          attributionControl={false}
          id="map"
          ref={this.mapRef}
          onClick={this.onMapClick}
          onZoomend={this.onMapChange}
          onMoveend={this.onMapChange}
          closePopupOnClick={false}
          onMouseMove={this.onMouseMoveHandler}
        >
          <TileLayer
            url={mapConfig.defaultBaseMap.uri}
            attribution={mapConfig.defaultBaseMap.params.attribution}
            onLoad={this.onMapLoaded}/>
          <PointDetails/>
          {active_indicator ?
           <div key={'point-details-' + active_indicator.prefix}>
             {!(playing || dragging) && this.renderIndicatorLayer(active_indicator, wms_time, 0.7)}
             <IndicatorOverlays/>
           </div> : ''}
          {
            active_boundaries.filter(b => b.type === 'wms').map(layer => (
              <WMSTileLayer
                key={layer.prefix}
                url={layer.source}
                layers={layer.params.layers}
                format={layer.params.format}
                transparent={layer.params.transparent}
                tiled={true}
                tilesorigin="0,0"
                zIndex={3}/>
            ))
          }
          {
            // We have to add the layer twice, once for the lines using the topomesh, and once for the labels.
            // The topomesh avoids an issue where the shared boundaries mess up the dashed lines.
            active_boundaries.filter(b => b.type === 'geojson' && b.geojson).map(boundary => (
              <LayerGroup key={boundary.prefix}>
                <GeoJSON
                  key={boundary.prefix}
                  data={boundary.topomesh}
                  style={boundary.style}
                  zIndex={3}
                />
                <GeoJSON
                  key={'labels_' + boundary.prefix}
                  data={boundary.geojson}
                  style={{ stroke: 0, fill: false }}
                  zIndex={3}
                  onEachFeature={(feature, layer) => this.addBoundaryLabel(feature, layer, boundary)}
                />
              </LayerGroup>
            ))
          }
          {this.renderOverlays(this.props)}
          <ManualWeatherLayer/>
          <IncidentsLayer/>
          <ScaleControl position="bottomleft" imperial={false}/>
          <ZoomControl position="bottomleft"/>
          <Control className="leaflet-control-location leaflet-bar" position="bottomleft">
            <a onClick={() => this.onLocationSearch()}>
              <i className={active_location_search ? 'fa fa-spinner fa-spin' : 'far fa-dot-circle'}/></a>
          </Control>
          <CoordinatesControl mapRef={this.mapRef} onMouseMoveHandler={this.onMouseMoveHandler}/>
          <Control position={'bottomright'}>
            <div className={'compass'}/>
          </Control>
          <Control className="leaflet-control-reset leaflet-bar" position="bottomleft">
            <a onClick={() => dispatch(resetMap())} title="Reset map zoom and centre">
              <i className="far fa-undo"/>
            </a>
          </Control>
        </Map>
      </div>
    );
  }

  renderIndicatorLayer(indicator, time, opacity) {
    let wmsTileLayer = null;
    if (indicator && time) {
      wmsTileLayer = <WMSTileLayer
        url={((indicator.base || geoserverBase) + indicator.source)}
        layers={indicator.params.layers}
        format={indicator.params.format}
        transparent={indicator.params.transparent}
        tileSize={indicator.params.tileSize || 256}
        time={time}
        DIM_ISSUE_TIME={indicator.dim_issue_time || ''}
        opacity={opacity}
        key={indicator.prefix + time}
        zIndex={2}/>;
    }
    return wmsTileLayer;
  }

  renderWindBarbLayer(wind_barb_data) {
    if (wind_barb_data) {
      return <GeoJSON key={wind_barb_data.key} data={wind_barb_data} pointToLayer={this.barbMarker} zIndex={4}/>;
    }
  }

  barbMarker = (feature, latlng) => {
    const icon = L.WindBarb.icon({
      mirrorVel: true,
      strokeWidth: 1,
      deg: feature.properties.deg,
      speed: feature.properties.speed,
      pointRadius: 4,
      barbHeight: 10
    });
    return L.marker(latlng, { icon });
  };

  renderOverlays(props) {
    const layers = props.overlays.filter(overlay => overlay.active).map((overlay) => {
      switch (overlay.id) {
        case OVERLAY_KEYS.WIND_BARBS:
          return this.renderWindBarbLayer(props.wind_barb_data);

        case OVERLAY_KEYS.WIND_ANIMATED:
          return props.dragging || props.playing ?
                 null :
                 this.renderWindLayer(props.wind_velocity_data, props.time.selected_time);

        case OVERLAY_KEYS.WEATHER_STATIONS:
          return this.renderAwsLayer(props.aws_data);

        case OVERLAY_KEYS.FUEL_MOISTURE_STATIONS:
          return this.renderFmsLayer(props.fms_data);

        default:
          return null;
      }
    });
    return <div>{layers}</div>;
  }

  renderWindLayer(wind_velocity_data, key) {
    if (wind_velocity_data) {
      return <VelocityLayer key={OVERLAY_KEYS.WIND_ANIMATED + key} data={wind_velocity_data}/>;
    }
    return null;
  }

  renderAwsLayer(aws_data) {
    if (aws_data && aws_data.aqfxLoaded) {
      return <GeoJSON key={OVERLAY_KEYS.WEATHER_STATIONS}
                      data={aws_data}
                      pointToLayer={this.awsMarker}
                      zIndex={5}
                      onEachFeature={(feature, layer) => this.markerPopup(layer, feature.properties.description)}/>;
    }
    return null;
  }

  renderFmsLayer(fms_data) {
    if (fms_data) {
      return <GeoJSON key={OVERLAY_KEYS.FUEL_MOISTURE_STATIONS}
                      data={fms_data}
                      pointToLayer={this.fmsMarker}
                      zIndex={5}
                      onEachFeature={(feature, layer) => this.markerPopup(layer, feature.properties.name)}/>;
    }
    return null;
  }

  awsMarker = (feature, latlng) => {
    const color = (feature.properties.isAqfx || feature.properties.hasActualAerologicalDiagram) ?
                  '#33AA33' :
                  feature.properties.isAutoWS ? '#3388ff' : '#8833ff';
    return L.circleMarker(latlng, { weight: 2, color }).setRadius(3);
  };

  fmsMarker = (feature, latlng) => {
    const color = '#ff3388';
    return L.circleMarker(latlng, { weight: 2, color }).setRadius(3);
  };

  markerPopup(layer, content) {
    layer.bindPopup(content, { closeButton: false, autoClose: false });
    layer.on('mouseover', () => layer.openPopup());
    layer.on('mouseout', () => layer.closePopup());
    layer.on('click', this.onMapClick);
  }
}

const mapStateToProps = state => {
  return {
    boundaries: state.boundaries.layers,
    active_indicator: state.indicators.active,
    overlays: state.overlays.layers,
    time: state.time,
    playing: state.play.playing,
    dragging: state.timeline_dnd.dragging,
    map_center: state.map.map_center,
    map_zoom: state.map.map_zoom,
    min_zoom: state.map.min_zoom,
    max_zoom: state.map.max_zoom,
    recentre_map: state.map.recentre_map,
    wind_velocity_data: state.wind_data.velocity_data,
    wind_barb_data: state.wind_data.barb_data,
    aws_data: state.aws.geo_json,
    fms_data: state.fms.geo_json,
    active_location_search: state.view.active_location_search
  };
};

export default connect(mapStateToProps)(MapLayers);



// WEBPACK FOOTER //
// ./src/containers/MapLayers.js